const PENDING_PROVIDER_CATEGORIES_KEY = "servix_pending_provider_categories";

type PendingProviderCategoriesEntry = {
	providerId: number;
	categoryIds: number[];
	createdAt: string;
};

function normalizeCategoryIds(categoryIds: number[]): number[] {
	return Array.from(
		new Set(
			categoryIds.filter(
				(categoryId) => Number.isInteger(categoryId) && Number(categoryId) > 0,
			),
		),
	);
}

function readPendingEntries(): PendingProviderCategoriesEntry[] {
	if (typeof window === "undefined") return [];
	try {
		const raw = window.localStorage.getItem(PENDING_PROVIDER_CATEGORIES_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((entry): entry is PendingProviderCategoriesEntry => {
			if (!entry || typeof entry !== "object") return false;
			const candidate = entry as Partial<PendingProviderCategoriesEntry>;
			return (
				typeof candidate.providerId === "number" &&
				candidate.providerId > 0 &&
				Array.isArray(candidate.categoryIds)
			);
		});
	} catch {
		return [];
	}
}

function writePendingEntries(entries: PendingProviderCategoriesEntry[]) {
	if (typeof window === "undefined") return;
	if (entries.length === 0) {
		window.localStorage.removeItem(PENDING_PROVIDER_CATEGORIES_KEY);
		return;
	}
	window.localStorage.setItem(
		PENDING_PROVIDER_CATEGORIES_KEY,
		JSON.stringify(entries),
	);
}

export function savePendingProviderCategories(
	providerId: number,
	categoryIds: number[],
) {
	if (!Number.isFinite(providerId) || providerId <= 0) return;
	const normalizedCategoryIds = normalizeCategoryIds(categoryIds);
	if (normalizedCategoryIds.length === 0) return;

	const entries = readPendingEntries().filter(
		(entry) => entry.providerId !== providerId,
	);

	entries.push({
		providerId,
		categoryIds: normalizedCategoryIds,
		createdAt: new Date().toISOString(),
	});

	writePendingEntries(entries);
}

export function getPendingProviderCategories(providerId: number): number[] | null {
	if (!Number.isFinite(providerId) || providerId <= 0) return null;
	const entries = readPendingEntries();
	const found = entries.find((entry) => entry.providerId === providerId);
	if (!found) return null;
	return normalizeCategoryIds(found.categoryIds);
}

export function clearPendingProviderCategories(providerId: number) {
	if (!Number.isFinite(providerId) || providerId <= 0) return;
	const entries = readPendingEntries();
	writePendingEntries(entries.filter((entry) => entry.providerId !== providerId));
}
